// src/admin/SuperAdminApp.js
import React, { useState } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Users, BarChart, Settings, Home, LogOut } from 'lucide-react';
import BusinessManager from './BusinessManager.js';
import SuperAdminDashboard from './SuperAdminDashboard';
import PlatformSettings from './PlatformSettings';
import PlatformAnalytics from './PlatformAnalytics';
import { useAuth } from '../auth/SupabaseAuthProvider';

const SuperAdminApp = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState('dashboard');

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navItems = [
    {
      id: 'dashboard',
      path: '/admin/dashboard',
      label: 'Dashboard',
      icon: Home,
    },
    {
      id: 'businesses',
      path: '/admin/businesses',
      label: 'Businesses',
      icon: Users,
    },
    {
      id: 'analytics',
      path: '/admin/analytics',
      label: 'Analytics',
      icon: BarChart,
    },
    {
      id: 'settings',
      path: '/admin/settings',
      label: 'Platform Settings',
      icon: Settings,
    },
  ];

  const handleNavClick = (id, path) => {
    setCurrentPage(id);
    navigate(path);
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Navigation header with SuperAdmin branding */}
      <header className="bg-indigo-700 text-white">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold">TrackerSheets Admin</h1>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex space-x-6">
              {navItems.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    onClick={() => handleNavClick(item.id, item.path)}
                    className={`flex items-center px-3 py-2 text-sm font-medium rounded-md ${
                      currentPage === item.id
                        ? 'bg-indigo-800 text-white'
                        : 'text-indigo-100 hover:text-white hover:bg-indigo-600'
                    }`}
                  >
                    <Icon className="mr-1.5 h-5 w-5" />
                    <span>{item.label}</span>
                  </button>
                );
              })}

              <button
                onClick={handleLogout}
                className="flex items-center px-3 py-2 text-sm font-medium rounded-md text-indigo-100 hover:text-white hover:bg-indigo-600"
              >
                <LogOut className="mr-1.5 h-5 w-5" />
                <span>Logout</span>
              </button>
            </nav>

            {/* Mobile menu button - simplified for this example */}
            <div className="md:hidden">
              <button className="text-white">
                <svg
                  className="h-6 w-6"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="min-h-screen pt-6">
        <Routes>
          <Route
            path="/"
            element={<Navigate to="/admin/dashboard" replace />}
          />
          <Route path="/dashboard" element={<SuperAdminDashboard />} />
          <Route path="/businesses" element={<BusinessManager />} />
          <Route path="/analytics" element={<PlatformAnalytics />} />
          <Route path="/settings" element={<PlatformSettings />} />
          <Route
            path="*"
            element={<Navigate to="/admin/dashboard" replace />}
          />
        </Routes>
      </main>
    </div>
  );
};

export default SuperAdminApp;
